import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { prepareRequest } from '@atlas/core';
import { Form } from '@atlas/react-forms';
import { Button, Label } from '@atlas/ui';

import ErrorFields from 'common/Form/ErrorFields';
import { fieldComponents } from 'common/Form/formConfig';
import CaptchaInput from 'common/Form/FormWithCaptcha/CaptchaInput';
import useFormCaptcha from 'common/Form/FormWithCaptcha/useFormCaptcha';
import GlobalErrors from 'common/Form/GlobalErrors';
import { getAreRequiredCheckboxesChecked } from 'common/utils/utils';

const templatesWithErrorFields = {
  BY_EMAIL: true,
  BY_PHONE: true,
  MULTI_STEP: true,
};

const ContainerComponent = ({
  bottomContent = null,
  buttonClassName,
  buttonId,
  buttonText,
  children,
  className,
  disableButton,
  disablePristine,
  fieldErrors,
  fields = [],
  footerContent = null,
  formNumber,
  formType,
  globalErrors,
  id,
  isLoading,
  isSubmitButtonAlwaysEnabled,
  leftSideSubmitContent,
  loadingDeferFields,
  loadingFields,
  onRequiredCheckboxesChange,
  onSetGlobalErrors,
  onSubmit,
  onTrackedFieldChanges,
  touchedFields,
  trackedFields,
  trailingCenterContent,
  validatedDeferFields,
  values,
}) => {
  const captchaRef = useRef();
  const {
    input: captchaInput,
    isRequired: isCaptchaRequired,
    key: captchaKey,
    setInput: onCaptchaChange,
  } = useFormCaptcha();

  const [errorFields, setErrorFields] = useState([]);
  const [showErrorFields, setShowErrorFields] = useState(false);

  const isFormInvalid =
    (isCaptchaRequired && !captchaInput) ||
    isLoading ||
    globalErrors.length ||
    Object.values(loadingFields).some(isLoading => isLoading) ||
    Object.values(fieldErrors).some(errorList => errorList.length !== 0) ||
    (disablePristine && Object.values(touchedFields).every(isTouched => !isTouched)) ||
    disableButton;

  const isDisabled = !isSubmitButtonAlwaysEnabled && isFormInvalid;

  const areRequiredCheckboxesChecked = getAreRequiredCheckboxesChecked(
    fields,
    field => values[field.name],
  );

  const handleCaptchaChange = useCallback(
    value => {
      onSetGlobalErrors([]);
      onCaptchaChange(value);
    },
    [onSetGlobalErrors, onCaptchaChange],
  );

  const trackValues = useRef({});

  useEffect(() => {
    if (!trackedFields || !onTrackedFieldChanges) {
      return;
    }

    trackedFields.forEach(fieldName => {
      const touched = touchedFields[fieldName];
      const errors = fieldErrors[fieldName];
      const checked = trackValues.current[fieldName];
      const value = values[fieldName];
      const valid = !errors || !errors.length;

      if (!checked && touched && valid && value) {
        // Track the completion of the valid value input
        // Broadcast changes only once
        onTrackedFieldChanges(fieldName);
        trackValues.current[fieldName] = true;
      }
    });
  }, [values, trackedFields, touchedFields, onTrackedFieldChanges, fieldErrors]);

  useEffect(() => {
    onRequiredCheckboxesChange?.(areRequiredCheckboxesChecked);
  }, [areRequiredCheckboxesChecked, onRequiredCheckboxesChange]);

  const handleErrorFields = useCallback(() => {
    setShowErrorFields(true);
    setErrorFields(
      fields.reduce((fieldsToReturn, field) => {
        const currentFieldErrors = fieldErrors[field.name];

        if (!currentFieldErrors || currentFieldErrors.length === 0) {
          return fieldsToReturn;
        }

        return [...fieldsToReturn, field.label];
      }, []),
    );
  }, [fieldErrors, fields]);

  const handleSubmitWithCaptcha = useCallback(
    async event => {
      if (isCaptchaRequired && !captchaInput) {
        return;
      }

      setShowErrorFields(false);
      onSubmit(event);
    },
    [isCaptchaRequired, captchaInput, onSubmit],
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' && event.shiftKey === false) {
        event.preventDefault();
        handleSubmitWithCaptcha(event);
      }
    },
    [handleSubmitWithCaptcha],
  );
  const formClassName = cx('form', className);

  useEffect(() => {
    setShowErrorFields(false);
  }, [formType?.id, formNumber]);

  useEffect(() => {
    if (isFormInvalid && validatedDeferFields && !loadingDeferFields) {
      handleErrorFields();
    }
  }, [handleErrorFields, isFormInvalid, loadingDeferFields, validatedDeferFields]);

  return (
    <form
      action="/form"
      autoComplete="off"
      className={formClassName}
      id={id}
      method="post"
      onKeyDown={handleKeyDown}
      role="application"
      tabIndex={0}
    >
      {showErrorFields &&
        errorFields.length > 0 &&
        templatesWithErrorFields[formType?.templateName] && (
          <ErrorFields errorFields={errorFields} shouldScroll={showErrorFields} />
        )}
      <div className="form-wrapper">
        {children}
        {isCaptchaRequired ? (
          <CaptchaInput captchaKey={captchaKey} onChange={handleCaptchaChange} ref={captchaRef} />
        ) : null}
        {trailingCenterContent !== null && <>{trailingCenterContent}</>}
        <GlobalErrors globalErrors={globalErrors} />
        {footerContent}
      </div>

      <div className="sticky-bottom-buttons-container">
        {leftSideSubmitContent}
        <Button
          className={cx('big', buttonClassName)}
          disabled={isDisabled || loadingDeferFields}
          id={buttonId}
          loading={isLoading || loadingDeferFields}
          onClick={handleSubmitWithCaptcha}
        >
          <Label message={buttonText} />
        </Button>
        {bottomContent}
      </div>
    </form>
  );
};

ContainerComponent.propTypes = {
  bottomContent: PropTypes.node,
  buttonClassName: PropTypes.string,
  buttonId: PropTypes.string,
  buttonText: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  disableButton: PropTypes.bool,
  disablePristine: PropTypes.bool,
  fieldErrors: PropTypes.object,
  fields: PropTypes.arrayOf(PropTypes.any),
  footerContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  formNumber: PropTypes.number,
  formType: PropTypes.object,
  globalErrors: PropTypes.arrayOf(PropTypes.object),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  isSubmitButtonAlwaysEnabled: PropTypes.bool,
  leftSideSubmitContent: PropTypes.node,
  loadingDeferFields: PropTypes.bool,
  loadingFields: PropTypes.object,
  onRequiredCheckboxesChange: PropTypes.func,
  onSetGlobalErrors: PropTypes.func,
  onSubmit: PropTypes.func,
  onTrackedFieldChanges: PropTypes.func,
  touchedFields: PropTypes.object,
  trackedFields: PropTypes.arrayOf(PropTypes.string),
  trailingCenterContent: PropTypes.node,
  values: PropTypes.object,
};

const FormCom = forwardRef(
  (
    {
      action,
      additionalValues,
      bottomContent,
      buttonClassName,
      buttonId,
      buttonText = '',
      className,
      disableButton = false,
      disablePristine,
      footerContent,
      form,
      formType,
      id,
      initialValues,
      isSubmitButtonAlwaysEnabled,
      leftSideSubmitContent,
      onError = () => {},
      onRequiredCheckboxesChange,
      onSubmit,
      onTrackedFieldChanges,
      trackedFields,
      trailingCenterContent,
      validateRequestHeaders,
    },
    ref,
  ) => {
    const validateFieldAsyncHandler = useCallback(
      async field => {
        const { disabled, name: fieldName, value } = field;

        if (disabled || value === null || value === undefined || value.length === 0) {
          return [];
        }

        const phoneValue = value.length < 3 && value[0] === '+' ? '' : value;

        const request = prepareRequest({
          ...(validateRequestHeaders && { headers: validateRequestHeaders }),
          method: 'POST',
          url: 'client/form:validate-field',
        });

        let errors = [];

        try {
          await request({
            fieldName,
            formId: id,
            value: fieldName === 'PHONE' ? phoneValue : value,
          });
        } catch (error) {
          const newErrors = error?.errors?.[0]?.violations;

          if (newErrors && newErrors.length) {
            errors = newErrors;
          }
        }

        return errors;
      },
      [id, validateRequestHeaders],
    );

    return (
      <Form
        ContainerComponent={ContainerComponent}
        ContainerComponentProps={{
          bottomContent,
          buttonClassName,
          buttonId,
          buttonText,
          className,
          disableButton,
          footerContent,
          formType,
          id: form.name,
          isSubmitButtonAlwaysEnabled,
          leftSideSubmitContent,
          onRequiredCheckboxesChange,
          onTrackedFieldChanges,
          trackedFields,
          trailingCenterContent,
        }}
        action={action}
        additionalValues={additionalValues}
        disableButton={disableButton}
        disablePristine={disablePristine}
        enableButton={isSubmitButtonAlwaysEnabled}
        fieldComponents={fieldComponents}
        form={form}
        initialValues={initialValues}
        onError={onError}
        onSubmit={onSubmit}
        onValidateFieldAsync={validateFieldAsyncHandler}
        ref={ref}
        steps={formType?.steps}
      />
    );
  },
);

FormCom.propTypes = {
  action: PropTypes.string,
  additionalValues: PropTypes.arrayOf(PropTypes.object),
  bottomContent: PropTypes.node,
  buttonClassName: PropTypes.string,
  buttonId: PropTypes.string,
  buttonText: PropTypes.string,
  className: PropTypes.string,
  disableButton: PropTypes.bool,
  disablePristine: PropTypes.bool,
  footerContent: PropTypes.node,
  form: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired,
    number: PropTypes.number.isRequired,
  }),
  formType: PropTypes.object,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialValues: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSubmitButtonAlwaysEnabled: PropTypes.bool,
  leftSideSubmitContent: PropTypes.node,
  onError: PropTypes.func,
  onRequiredCheckboxesChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onTrackedFieldChanges: PropTypes.func,
  trackedFields: PropTypes.arrayOf(PropTypes.string),
  trailingCenterContent: PropTypes.node,
};

export default FormCom;
